import { useState } from 'react';
import { Droppable, DraggableProvided } from 'react-beautiful-dnd';
import { MdDragIndicator, MdEdit } from 'react-icons/md';
import { BiTrash } from 'react-icons/bi';
import { FiChevronDown } from 'react-icons/fi';
import { isActiveClass } from 'utils';
import { SectionBlock, getScenarioTitleFromQuestion, getQuestionScenarioId } from 'utils/examScenario';
import { Question } from 'api/questions/types';
import { ExamScenarioQuestionRow } from './ExamScenarioQuestionRow';

interface Props {
  sectionId: number;
  blockIndex: number;
  block: SectionBlock;
  sectionQuestions: Question[];
  sIndex: number;
  blockDragHandleProps?: DraggableProvided['dragHandleProps'];
  onMoveBlock: (sIndex: number, block: SectionBlock) => void;
  onEdit: (q: Question) => void;
  onDelete: (sIndex: number, questionId: string) => void;
  onDeleteBlock: (sIndex: number, block: SectionBlock) => void;
  onEditScenario: (scenarioId: string) => void;
}

export function ExamScenarioBlock({
  sectionId,
  blockIndex,
  block,
  sectionQuestions,
  sIndex,
  blockDragHandleProps,
  onMoveBlock,
  onEdit,
  onDelete,
  onDeleteBlock,
  onEditScenario
}: Props) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const scenarioTitle = getScenarioTitleFromQuestion(block.questions[0]);
  const scenarioId = block.questions[0] ? getQuestionScenarioId(block.questions[0]) : null;

  return (
    <div className="space-y-2 rounded-md bg-tertiary/10">
      <div
        className="text-sm font-medium grid items-center gap-2"
        style={{ gridTemplateColumns: 'auto 1fr auto auto auto auto' }}
      >
        <div className="cursor-move" {...blockDragHandleProps}>
          <MdDragIndicator className="w-5 h-5 text-secondary" />
        </div>
        <span className="text-black/90">
          Scenario{scenarioTitle ? `: ${scenarioTitle}` : ' questions'}
        </span>
        <button
          type="button"
          className="text-xs outlinedBtn py-1 px-2"
          onClick={() => onMoveBlock(sIndex, block)}
          title="Move scenario block to another section"
        >
          Move block
        </button>
        {scenarioId && (
          <button
            type="button"
            className="p-1 rounded hover:bg-black/5"
            onClick={() => onEditScenario(scenarioId)}
            title="Edit scenario"
          >
            <MdEdit className="w-5 h-5 text-secondary" />
          </button>
        )}
        <button
          type="button"
          className="p-1 rounded hover:bg-black/5"
          onClick={() => onDeleteBlock(sIndex, block)}
          title="Delete scenario block"
        >
          <BiTrash className="w-5 h-5 text-danger" />
        </button>
        <FiChevronDown
          className={`transition-all duration-200 ease-in-out w-5 h-5 cursor-pointer shrink-0 ${isActiveClass(
            !isCollapsed,
            'transform rotate-180'
          )}`}
          onClick={() => setIsCollapsed((c) => !c)}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        />
      </div>
      {!isCollapsed && (
        <Droppable
          droppableId={`blockQuestions-${sectionId}-${blockIndex}`}
          type={`blockQuestions-${sectionId}-${blockIndex}`}
        >
          {(provided) => (
            <ul
              ref={provided.innerRef}
              className="pl-7 space-y-2"
              {...provided.droppableProps}
            >
              {block.questions.map((q, qInBlock) => (
                <ExamScenarioQuestionRow
                  key={q._id}
                  sectionId={sectionId}
                  blockIndex={blockIndex}
                  question={q}
                  qInBlock={qInBlock}
                  sectionQuestions={sectionQuestions}
                  sIndex={sIndex}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      )}
    </div>
  );
}
